import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { ToggleGroup, YesNoToggle } from "./ToggleGroup";
import type { StampDutyInputs, PropertyType, PurchaseType } from "../_types/calculator.types";

interface StampDutyFormProps {
  inputs: StampDutyInputs;
  onUpdate: <K extends keyof StampDutyInputs>(key: K, value: StampDutyInputs[K]) => void;
}

/**
 * Format number with commas only (no currency symbol)
 */
function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-AU").format(value);
}

/**
 * Parse number string to number
 */
function parseNumber(value: string): number {
  const cleaned = value.replace(/[^0-9]/g, "");
  return parseInt(cleaned, 10) || 0;
}

/**
 * Currency input component with better UX
 */
function CurrencyInput({
  value,
  onChange,
  label,
  presets,
}: {
  value: number;
  onChange: (value: number) => void;
  label: string;
  presets?: number[];
}) {
  const [displayValue, setDisplayValue] = useState(formatNumber(value));
  const [isFocused, setIsFocused] = useState(false);

  // Sync display value when external value changes (and not focused)
  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(formatNumber(value));
    }
  }, [value, isFocused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Allow only numbers and commas while typing
    const cleaned = raw.replace(/[^0-9,]/g, "");
    setDisplayValue(cleaned);

    // Update the actual value
    const numValue = parseNumber(cleaned);
    if (numValue !== value) {
      onChange(numValue);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Remove formatting on focus for easier editing
    setDisplayValue(value.toString());
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Re-format on blur
    setDisplayValue(formatNumber(value));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="relative flex-shrink-0">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
            $
          </span>
          <Input
            type="text"
            inputMode="numeric"
            value={displayValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className="w-44 pl-7 text-right font-semibold text-lg h-12 bg-muted/30"
            aria-label={label}
          />
        </div>
        {presets && presets.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {presets.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => {
                  onChange(preset);
                  setDisplayValue(formatNumber(preset));
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  value === preset
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground"
                }`}
              >
                {preset >= 1000000
                  ? `$${(preset / 1000000).toFixed(1)}M`
                  : `$${preset / 1000}K`}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Form row component for consistent layout
 */
function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3 py-5 border-b border-border/50 last:border-b-0">
      <label className="text-sm font-medium text-foreground">{label}</label>
      <div>{children}</div>
    </div>
  );
}

/**
 * Stamp duty calculator form
 */
export function StampDutyForm({ inputs, onUpdate }: StampDutyFormProps) {
  const propertyTypeOptions: { value: PropertyType; label: string }[] = [
    { value: "primary", label: "Primary residence" },
    { value: "investment", label: "Investment" },
  ];

  const purchaseTypeOptions: { value: PurchaseType; label: string }[] = [
    { value: "established", label: "Established Home" },
    { value: "new", label: "New Home" },
    { value: "vacant", label: "Vacant Land" },
  ];

  const dependentOptions = [
    { value: "0", label: "0" },
    { value: "1", label: "1" },
    { value: "2", label: "2" },
    { value: "3", label: "3" },
    { value: "4", label: "4" },
    { value: "5+", label: "5+" },
  ];

  const propertyPresets = [500000, 750000, 1000000, 1500000];
  const incomePresets = [80000, 120000, 180000, 250000];

  return (
    <div>
      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2">
        Enter your details
      </h3>

      <div className="divide-y divide-border/50">
        <FormRow label="Are you a first home buyer?">
          <YesNoToggle
            value={inputs.isFirstHomeBuyer}
            onChange={(v) => onUpdate("isFirstHomeBuyer", v)}
          />
        </FormRow>

        <FormRow label="Value of Property">
          <CurrencyInput
            value={inputs.propertyValue}
            onChange={(v) => onUpdate("propertyValue", v)}
            label="Property value"
            presets={propertyPresets}
          />
        </FormRow>

        <FormRow label="Property type">
          <ToggleGroup
            options={propertyTypeOptions}
            value={inputs.propertyType}
            onChange={(v) => onUpdate("propertyType", v)}
          />
        </FormRow>

        <FormRow label="What are you purchasing?">
          <ToggleGroup
            options={purchaseTypeOptions}
            value={inputs.purchaseType}
            onChange={(v) => onUpdate("purchaseType", v)}
          />
        </FormRow>

        <FormRow label="Are you an eligible pensioner?">
          <YesNoToggle
            value={inputs.isPensioner}
            onChange={(v) => onUpdate("isPensioner", v)}
          />
        </FormRow>

        <FormRow label="Total income of all purchasers">
          <CurrencyInput
            value={inputs.totalIncome}
            onChange={(v) => onUpdate("totalIncome", v)}
            label="Total income"
            presets={incomePresets}
          />
        </FormRow>

        <FormRow label="Number of dependent children">
          <ToggleGroup
            options={dependentOptions}
            value={inputs.dependentChildren >= 5 ? "5+" : String(inputs.dependentChildren)}
            onChange={(v) => onUpdate("dependentChildren", v === "5+" ? 5 : parseInt(v, 10))}
          />
        </FormRow>
      </div>
    </div>
  );
}
